/**
 * Taking part of a leg off, at a price or at market — ROADMAP 5.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT THE CHAIN'S LIMIT TICKET WITH THE SIDE FLIPPED
 *
 * The obvious build is to reuse `LimitOrderTicket`, default the side to the
 * closing one, and be done. It would send the wrong thing.
 *
 * That ticket places an ENTRY carrying a `premiumOrder` — an app-managed
 * limit that opens a NEW Trade. A SELL from it against a long leg does not
 * reduce that leg: it files a second, opposite position that offsets on
 * screen and nowhere else. The fill lands on a new `TradeLeg`, the original
 * stays open on the record, its stop stays sized for the whole of it, and
 * the journal shows two trades where one was meant.
 *
 * An exit is a different ACTION, not an entry pointed the other way:
 *
 *   it names the leg     `OrderIntentLeg.tradeLegId` is what the fill folds
 *                        by, and without it `writeLegProjection` SKIPS the
 *                        fill entirely (the four symptoms of 7.7)
 *   it is sized server-side  from a read taken at the moment of acting
 *   it is kind EXIT      so it claims an engine lane ahead of entries and
 *                        the journal can say what it was for
 *
 * So this dialog posts to the EXIT path, and the side is not a control at
 * all — it is whatever closes the leg, which the server decides from the
 * leg's own sign.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PERCENTAGE IS THE CONTROL, AND THE QUANTITY IS A PREVIEW
 *
 * The lots shown here are computed from the book this drawer last read, and
 * they are never sent. The request carries 25/50/75/100 and the server
 * resolves it against its own read — see `partialExit.ts`. Between the read
 * and the click a stop can fire or another tab can trade the account, and a
 * browser that asserted "sell 2 lots" against a leg now holding 1 would open
 * a short from a button labelled Exit.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  EXIT_FRACTIONS,
  exitFractionLabel,
  exitLots,
  scaleInLots,
  type ExitFraction,
} from '@apna/shared'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { useTickLtps } from '@/feed/useTick'
import { cn } from '@/lib/utils'
import { expiryLabel } from '@/lib/fmt'
import { placeAnchoredPanel, samePlacement, type PanelPlacement } from './anchoredPanel'
import { tradeName, type OpenBookLeg, type OpenBookTrade } from './useOpenBook'

export interface ExitTicketTarget {
  readonly trade: OpenBookTrade
  /** Null is the WHOLE basket — 9.36. See `basket` in the body. */
  readonly leg: OpenBookLeg | null
  /**
   * The button that opened it — 9.16. The ticket is placed under this, not
   * in the middle of the screen: it is opened from a 24-pixel control on a
   * leg row, and a centred dialog is a screen's width of mouse travel each
   * way on a wide monitor.
   *
   * The ELEMENT and not a rect, because the rect must be taken after the
   * dialog has mounted — Radix locks the page scroll on open and that can
   * move everything by a scrollbar's width.
   */
  readonly anchor: HTMLElement
  /** Fresh per opening, so a double-send deduplicates against one action. */
  readonly requestId: string
}

/**
 * How wide the ticket wants to be.
 *
 * HERE AND NOT IN A CLASS, because the placement sets `maxWidth` inline and
 * an inline style beats `sm:max-w-[420px]`. Two numbers meaning one thing is
 * how a panel ends up full-screen on a monitor nobody tested on.
 */
const TICKET_WIDTH = 300

/**
 * Roughly how tall the ticket is, before anything has been measured.
 *
 * Used for ONE frame, to decide whether the first render opens below the
 * button or above it. The measurement that follows in `useLayoutEffect`
 * corrects it before paint, so being a few pixels out costs nothing — and
 * being roughly right is what lets the panel exist from the first frame
 * instead of waiting, invisible, for a measurement that might not come.
 */
const TICKET_HEIGHT_GUESS = 250

/**
 * Which way this ticket sizes the position — ROADMAP 9.61.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DIRECTIONS, ONE FRACTION, AND THE SAME BASE FOR BOTH
 *
 * `OUT` books a fraction of what is held, which is what this ticket has
 * always done. `IN` adds that same fraction MORE of it: holding 4 lots,
 * 25% out books 1 and 25% in buys 1. The base is the current holding in
 * both directions, so one button means one thing — the owner's choice on
 * 2026-11-05, over sizing against the original entry, which is a figure
 * the ticket does not show and which stops matching after the first clip.
 *
 * Repeated scale-ins therefore compound: 4 -> 5 -> 6 -> 8. That is the
 * honest consequence of the base being "what you hold", and it is the same
 * arithmetic a trader does in their head when they say "add a quarter".
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type ExitDirection = 'OUT' | 'IN'

export interface ExitTicketRequest {
  readonly tradeId: string
  /** 9.61. `IN` adds to the position instead of booking part of it. */
  readonly direction: ExitDirection
  /** Absent means the WHOLE basket — 9.36. */
  readonly tradeLegId?: string
  readonly connectionId: string
  readonly fraction: ExitFraction
  /** Absent means MARKET — "get me out", priced at dispatch. */
  readonly limitPrice?: number
  /**
   * For the toast and the order book row. NOT an idempotency key — the exit
   * hook mints that itself, because it also owns the pending map that stops
   * a second press sizing against a leg whose first exit has not landed.
   */
  readonly label: string
}

export function ExitTicket({
  target,
  onClose,
  onSubmit,
}: {
  target: ExitTicketTarget | null
  onClose: () => void
  onSubmit: (req: ExitTicketRequest) => Promise<{ ok: boolean; reason?: string }>
}) {
  return target ? (
    <Ticket key={target.requestId} target={target} onClose={onClose} onSubmit={onSubmit} />
  ) : null
}

/**
 * Keep the panel under its button, and keep it on screen — 9.16.
 *
 * MEASURED, NOT ASSUMED. The panel's natural height depends on what is in
 * it: a resting exit grows a price field, a refusal grows an error line.
 * Hard-coding a height would mean the flip decision was made against a
 * number that is wrong half the time, so the panel is rendered, measured,
 * and placed — all before paint, in `useLayoutEffect`, so the trader never
 * sees it in the wrong spot.
 *
 * `scrollHeight` rather than `getBoundingClientRect().height`, because once
 * a `maxHeight` has been applied the rect reports the CAPPED height. Asking
 * for the rect on the second pass would read back our own constraint and
 * conclude the panel had shrunk enough to fit — a feedback loop that leaves
 * a panel permanently smaller than it needs to be.
 *
 * RE-RUN ON RESIZE, because a trader dragging the window or the drawer
 * split is exactly the case this has to survive. Not on scroll: the dialog
 * is modal, so nothing behind it can be scrolled while it is open.
 */
function useAnchoredTo(anchor: HTMLElement): {
  panelRef: React.RefObject<HTMLDivElement | null>
  bodyRef: React.RefObject<HTMLFormElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
  headerRef: React.RefObject<HTMLElement | null>
  placement: PanelPlacement
} {
  const panelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLFormElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  /** The one piece of chrome above the body. Its height never moves. */
  const headerRef = useRef<HTMLElement>(null)

  /*
   * ── PLACED BEFORE IT IS MEASURED, AND NEVER NULL ───────────────────────
   *
   * This started as `useState<PanelPlacement | null>(null)`, with the panel
   * held `invisible` until the first measurement arrived. That is one state
   * too many, and it failed in the way extra states do: the ticket stopped
   * appearing at all. Measurement needs three refs and runs once, so any
   * reason for one of them to be absent on that single pass — and a portal
   * inside an animated presence wrapper is a good place to find one — left
   * `placement` null forever, with no error and nothing on screen.
   *
   * There is no need for the state. The anchor is a live element and the
   * window knows its own size, so a placement can be computed DURING the
   * first render from a guessed height. The guess only decides the first
   * frame; the measurement that follows corrects it before paint.
   *
   * A panel in roughly the right place is always better than no panel, and
   * "roughly" is the worst case — the guess is only wrong when the trader
   * has switched to a resting exit, which they cannot have done yet.
   */
  const [placement, setPlacement] = useState<PanelPlacement>(() =>
    placeAnchoredPanel(
      anchor.getBoundingClientRect(),
      { width: TICKET_WIDTH, height: TICKET_HEIGHT_GUESS },
      { width: window.innerWidth, height: window.innerHeight },
      // Six controls in a fixed form. It overlaps the row rather than
      // growing a scrollbar — see `preferOverlap`.
      { preferOverlap: true },
    ),
  )

  const measure = useCallback(() => {
    const panel = panelRef.current
    const body = bodyRef.current
    const content = contentRef.current
    if (!panel || !body || !content) return
    /*
     * HOW TALL THE PANEL WOULD BE IF NOTHING CONSTRAINED IT, which is not
     * the same as how tall it is.
     *
     * Reading `panel.scrollHeight` is the obvious version and is wrong on
     * the second pass: once a cap is applied the BODY becomes the scroll
     * container, so the panel's own scrollHeight settles to the capped
     * figure and the measurement agrees with whatever it was last told. A
     * window dragged taller would then never let the panel grow back.
     *
     * Everything that is NOT the scrolling body — the header, the borders —
     * is `panel.offsetHeight - body.clientHeight`, and that figure does not
     * move when the body is capped. Adding the content's own height gives
     * the natural height, under any cap and after any content change.
     */
    /*
     * ── NOTHING HERE MAY BE DERIVED FROM THE CAP — ROADMAP 9.59 ────────
     *
     * The chrome used to be `panel.offsetHeight - body.clientHeight`, and
     * both of those move with the cap this function is computing. When
     * the body had a pixel of slack, `clientHeight` absorbed it and the
     * chrome came out a pixel bigger; the next pass then asked for a
     * pixel more, and the pass after that another. Measured settling at
     * 283 against a 282 content — a one-pixel scrollbar, which is still a
     * scrollbar and is the thing this exists to prevent.
     *
     * So every term is taken from an element whose height the cap does
     * NOT change: the panel's own border, the header, and the content
     * box. Same reading on every pass, whatever the cap currently is.
     *
     * AND ALL OF THEM ARE `offsetHeight`, never a rect. The dialog enters
     * on a `zoom-in`, and `getBoundingClientRect` reports the SCALED size
     * during the animation — measured 270px for a 284px panel, with the
     * cap then stuck there. Offset heights are layout, not paint.
     */
    const border = panel.offsetHeight - panel.clientHeight
    const next = placeAnchoredPanel(
      anchor.getBoundingClientRect(),
      {
        width: TICKET_WIDTH,
        height: border + (headerRef.current?.offsetHeight ?? 0) + content.offsetHeight,
      },
      { width: window.innerWidth, height: window.innerHeight },
      { preferOverlap: true },
    )
    // Keeping the previous OBJECT is what makes React bail out — see
    // `samePlacement`. Without it, measuring is a render loop.
    setPlacement((prev) => (samePlacement(prev, next) ? prev : next))
  }, [anchor])

  useLayoutEffect(() => {
    /*
     * ── IT HAS TO WAIT FOR THE NODES — ROADMAP 9.59 ────────────────────
     *
     * `measure` gives up when any of its three refs is null, and the
     * observer below is attached only when `contentRef` is set. On mount
     * both were true: the panel is a portal inside an animated presence
     * wrapper, so its content commits after this effect runs, and React
     * attaches refs bottom-up anyway — `contentRef` can be live while
     * `panelRef` is still null in the same pass.
     *
     * The consequence was not a wrong placement, it was NO placement: the
     * first measurement never happened, the observer was never attached,
     * and nothing re-measured afterwards. The panel kept its first-render
     * guess forever.
     *
     * Measured, 2026-11-05: cap 250px — `TICKET_HEIGHT_GUESS` exactly —
     * against a natural height of 282, so the body scrolled by 34px on a
     * ticket with six controls and room to spare. Dispatching a `resize`
     * by hand corrected it to 284 and the scrollbar went, which is what
     * proved the arithmetic was right all along and only the trigger was
     * missing.
     *
     * So it retries on animation frames until the nodes are there. The
     * frame is cancelled on unmount, and `attached` stops a late frame
     * observing a node belonging to a ticket that has already closed.
     */
    let frame = 0
    let attached = false
    const watcherOrNull =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => { measure() })

    const attach = (): void => {
      const content = contentRef.current
      if (!panelRef.current || !bodyRef.current || !content) {
        frame = requestAnimationFrame(attach)
        return
      }
      attached = true
      measure()
      watcherOrNull?.observe(content)
    }
    attach()

    /*
     * ── WHAT TRIGGERS A RE-MEASURE, AND WHAT MUST NOT ──────────────────
     *
     * This used to run on every render, which read layout on every render,
     * and this component re-renders on every TICK — `useTickLtps` is
     * subscribed to the leg's price. A `getBoundingClientRect` per tick is
     * a forced synchronous layout per tick, on the busiest screen in the
     * product, for an answer that had not changed.
     *
     * A `ResizeObserver` on the content asks the right question instead:
     * re-place when the panel's natural height actually changes — the
     * trader switching to Limit, a refusal appearing — and never because a
     * price moved. Observed on the CONTENT and not the body, because a
     * capped body stays exactly the same size while the content inside it
     * grows, so observing the body would miss the one case it is for.
     */
    window.addEventListener('resize', measure)
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      if (!attached) frame = 0
      watcherOrNull?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  return { panelRef, bodyRef, contentRef, headerRef, placement }
}

function Ticket({
  target,
  onClose,
  onSubmit,
}: {
  target: ExitTicketTarget
  onClose: () => void
  onSubmit: (req: ExitTicketRequest) => Promise<{ ok: boolean; reason?: string }>
}) {
  const { trade, leg } = target
  /*
   * ── 9.36. ONE TICKET, A LEG OR THE WHOLE BASKET ────────────────────────
   *
   * The card header used to carry its own 25/50/75/100 strip, which was a
   * second way of asking the same question in a different shape. A trader
   * who had learned the leg's ticket had to learn a second control to do
   * the same thing one level up.
   *
   * `leg === null` is the basket, and it differs in exactly two ways: there
   * is no single contract to name, and there is NO LIMIT — a basket exits
   * at market or not at all. A resting price only means something against
   * one contract; across five legs "limit 500" is five different trades
   * dressed as one instruction.
   */
  const basket = leg === null
  const { panelRef, bodyRef, contentRef, headerRef, placement } = useAnchoredTo(target.anchor)
  const [fraction, setFraction] = useState<ExitFraction>(1)
  /*
   * ── SCALE OUT BY DEFAULT — ROADMAP 9.61 ───────────────────────────────
   *
   * Booking is the safe direction and the common one, and this control is
   * reached from a cross on a position row — a place a trader arrives at
   * to reduce. Opening on IN would make an accidental return press ADD
   * risk, which is the one mistake this ticket must not make easy.
   */
  const [direction, setDirection] = useState<ExitDirection>('OUT')
  const [resting, setResting] = useState(false)
  const [price, setPrice] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)

  /*
   * THE SIDE IS NOT A CONTROL. A long is closed by selling and a short by
   * buying; offering the choice would offer the wrong one, and the server
   * derives it from the leg's own sign regardless of anything sent.
   */
  const held = trade.legs.filter((l) => l.netQty !== 0)
  /*
   * A basket has no single side — a condor closes two ways at once — so it
   * is not claimed. One leg does, and it is not a control: a long closes by
   * selling and a short by buying, and the server derives it from the leg's
   * own sign regardless of anything sent.
   */
  const closingSide = leg === null ? null : leg.netQty > 0 ? 'SELL' : 'BUY'
  const openLots = leg === null
    ? 0
    : Math.floor(Math.abs(leg.netQty) / Math.max(1, leg.lotSize))
  const lots = useMemo(() => exitLots(openLots, fraction), [openLots, fraction])
  /** 9.61. What an add of this fraction comes to, in whole lots. */
  const addLots = useMemo(
    () => (leg === null ? 0 : scaleInLots(leg, fraction)),
    [leg, fraction],
  )
  const qty = lots * (leg?.lotSize ?? 0)

  /*
   * ── SCALE IN WORKS ON A BASKET TOO — ROADMAP 9.61 ─────────────────────
   *
   * It was refused at first because an entry only joins an existing leg
   * through `manualNetting`, which turns a grouped one away — so the order
   * would have opened a stray position beside the condor rather than
   * growing it.
   *
   * `scaleInTrade` does not go through netting. It names the legs from the
   * trader's own record, server-side, and links the fills to them, so a
   * basket grows in place. Nothing about the netting rule was relaxed to
   * get there; see that service's header.
   *
   * THE SHAPE CAN STILL CHANGE, and the trader is told rather than
   * stopped. A fraction applied per leg does not always preserve a ratio —
   * a 2:1 spread scaled 25% adds 1:1 and becomes 3:2 — and that is the
   * trader's call to make. The adjustment is recorded and shown in the
   * journal.
   *
   * What is still refused is a leg with no contract on this account, which
   * is not a policy at all, just nothing to send.
   */
  const scaleIn: { allowed: true } | { allowed: false; why: string } =
    leg !== null && leg.token === null
      ? { allowed: false, why: 'This leg has no tradeable contract on this account.' }
      : held.length === 0
        ? { allowed: false, why: 'Nothing here is held, so there is nothing to add to.' }
        : { allowed: true }

  /** Resting protection that a size change will force to be re-placed. */
  const armedCount = held.filter((l) => l.slSpec !== null || l.tgtSpec !== null).length

  /** True when an add here would change the structure's own proportions. */
  const ratioShifts =
    leg === null &&
    direction === 'IN' &&
    new Set(
      held.map((l) => scaleInLots(l, fraction) / Math.max(1, Math.abs(l.netQty) / Math.max(1, l.lotSize))),
    ).size > 1

  /*
   * A refusal must not leave the ticket pointed at a direction it cannot
   * send. The toggle is disabled, and this catches the case where the leg
   * changes under an already-open ticket.
   */
  useEffect(() => {
    if (direction === 'IN' && scaleIn.allowed === false) setDirection('OUT')
  }, [direction, scaleIn.allowed])

  const ltps = useTickLtps(leg === null || leg.token === null ? [] : [leg.token])
  const ltp = leg === null || leg.token === null ? null : (ltps.get(leg.token) ?? null)

  const submit = async () => {
    if (busy.current) return
    const limit = price.trim() === '' ? null : Number(price)
    if (resting && !(limit !== null && Number.isFinite(limit) && limit > 0)) {
      setError('Set a price for a resting exit, or switch to market.')
      return
    }
    busy.current = true
    setSaving(true)
    setError(null)
    try {
      const result = await onSubmit({
        tradeId: trade.tradeId,
        direction,
        ...(leg === null ? {} : { tradeLegId: leg.tradeLegId }),
        connectionId: trade.broker.connectionId,
        fraction,
        ...(!basket && resting && limit !== null ? { limitPrice: limit } : {}),
        label:
          leg === null
            ? `Book ${exitFractionLabel(fraction)} of ${tradeName(trade) ?? trade.underlying}`
            : `${closingSide} ${exitFractionLabel(fraction)} × ${leg.symbol}`,
      })
      if (result.ok) onClose()
      else setError(result.reason ?? 'The exit was not accepted.')
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  const field =
    'num h-8 w-full rounded-md border border-border bg-background px-2.5 text-[12.5px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 disabled:opacity-50'

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose() }}>
      <DialogContent
        ref={panelRef}
        /*
         * ANCHORED, NOT CENTRED — 9.16. The primitive centres itself with
         * `top/left 50%` and a pair of translates, so all four have to be
         * neutralised before the measured coordinates mean anything: the
         * translates are the ones that bite, because a class that is still
         * applied moves the panel half its own size off the mark.
         *
         * `transition-none` IS LOAD-BEARING, AND NOT OBVIOUS. The primitive
         * carries `duration-200` and nothing else — no `transition-*`
         * property — and the CSS initial value of `transition-property` is
         * `all`. So every property it is given animates for 200 ms,
         * including `top` and `left`, and the panel SLID from wherever it
         * first rendered to its measured position: reported as "arriving
         * from the top left corner, too much lagging", and it was neither a
         * slow measurement nor a slow render. Placement is a jump, never a
         * journey — the enter animation is the only motion here.
         *
         * There is no `invisible` state and no null placement. The panel is
         * positioned from its first render — see `useAnchoredTo`.
         */
        className="flex w-full translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden p-0 transition-none"
        style={{
          top: placement.top,
          left: placement.left,
          maxHeight: placement.maxHeight,
          maxWidth: placement.width,
          /*
           * The primitive's zoom-in grows from the panel's own centre.
           * Growing from the edge nearest the button is the difference
           * between a panel that appeared and a panel that came out of the
           * thing you pressed.
           */
          transformOrigin: placement.side === 'below' ? 'top center' : 'bottom center',
        }}
        showCloseButton={!saving}
        onEscapeKeyDown={(e) => { if (saving) e.preventDefault() }}
        onPointerDownOutside={(e) => { if (saving) e.preventDefault() }}
      >
        <header ref={headerRef} className="shrink-0 border-b border-border px-3.5 py-2.5">
          <DialogTitle className="sr-only">Exit position</DialogTitle>
          <DialogDescription className="sr-only">
            Close part or all of this leg, at market or at a resting price.
          </DialogDescription>
          <div
            className={cn(
              'pr-6 text-[9px] font-semibold uppercase tracking-[0.14em]',
              direction === 'IN' ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {direction === 'IN' ? 'Add to position' : 'Exit position'}
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5 text-[13.5px] leading-tight">
            {leg === null ? (
              <span className="truncate font-semibold">
                {tradeName(trade) ?? `${trade.underlying} basket`}
              </span>
            ) : (
              <>
                <span
                  className={cn(
                    'rounded px-1 py-0.5 text-[9px] font-bold',
                    closingSide === 'SELL'
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-success/10 text-success',
                  )}
                >
                  {closingSide}
                </span>
                <span className="num font-semibold tracking-tight">
                  {leg.strike.toLocaleString('en-IN')}
                </span>
                <span className="font-semibold">{leg.optionType}</span>
              </>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-muted-foreground">
            {leg !== null && <span>{expiryLabel(leg.expiry)}</span>}
            {leg !== null && <span aria-hidden="true">·</span>}
            <span>{trade.broker.label}</span>
            <span aria-hidden="true">·</span>
            {/* What is actually held, so the percentage below has a subject. */}
            <span className="num">
              {leg === null
                ? `${held.length} leg${held.length === 1 ? '' : 's'} held`
                : `holding ${Math.abs(leg.netQty).toLocaleString('en-IN')} (${openLots} lot${openLots === 1 ? '' : 's'})`}
            </span>
          </div>
        </header>

        {/*
          * THE BODY SCROLLS, NOT THE WINDOW. When neither side of the
          * button had room for the whole panel, `placeAnchoredPanel`
          * caps its height — and a capped panel with an `overflow-hidden`
          * body would simply cut the Exit button off the bottom, which is
          * the failure this was supposed to prevent.
          */}
        <form ref={bodyRef} onSubmit={(e) => { e.preventDefault(); void submit() }} className="min-h-0 flex-1 overflow-y-auto">
          {/*
            * The padding moved in here from the form so the scrollbar — on
            * the days the panel has to shrink — runs down the panel's own
            * edge rather than floating five pixels inside it. This is also
            * the box the `ResizeObserver` watches: its height is the
            * panel's natural content height, whatever the cap above it.
            */}
          <div ref={contentRef} className="p-3.5">
          <fieldset disabled={saving} className="space-y-3">
            {/*
              ── WHICH WAY — ROADMAP 9.61 ──────────────────────────────────
              First, above the size, because it changes what the size MEANS:
              25% booked and 25% added are opposite trades, and a control
              that read the same for both would be the worst kind of
              ambiguity on a screen that sends orders without confirming.

              The whole panel takes its colour from it. Red is leaving a
              position and blue is entering one, which is the pairing the
              chain's own B and S already use, so the ticket reads the same
              way as the thing that opened it.
            */}
            <div
              className="grid grid-cols-2 gap-1 rounded-md border border-border bg-secondary/50 p-1"
              role="group"
              aria-label="Scale out of the position, or into it"
            >
              {(['OUT', 'IN'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={direction === d}
                  disabled={d === 'IN' && scaleIn.allowed === false}
                  title={d === 'IN' && scaleIn.allowed === false ? scaleIn.why : undefined}
                  data-testid={`exit-direction-${d.toLowerCase()}`}
                  onClick={() => setDirection(d)}
                  className={cn(
                    'rounded py-1 text-[11px] font-semibold transition-colors disabled:opacity-40',
                    direction === d
                      ? d === 'OUT'
                        ? 'bg-destructive/10 text-destructive shadow-sm'
                        : 'bg-primary/10 text-primary shadow-sm'
                      : 'text-muted-foreground',
                  )}
                >
                  {d === 'OUT' ? 'Scale out' : 'Scale in'}
                </button>
              ))}
            </div>
            {direction === 'IN' && scaleIn.allowed === false && (
              <p className="text-[10.5px] text-warning" data-testid="exit-scalein-refused">
                {scaleIn.why}
              </p>
            )}

            <div className="grid gap-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                How much
              </span>
              <div
                className="grid grid-cols-4 gap-1 rounded-md border border-border bg-secondary/50 p-1"
                role="group"
                aria-label={direction === 'OUT' ? 'How much of the position to close' : 'How much to add to the position'}
              >
                {EXIT_FRACTIONS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={fraction === f}
                    data-testid={`exit-fraction-${Math.round(f * 100)}`}
                    onClick={() => setFraction(f)}
                    className={cn(
                      'rounded py-1 text-[11px] font-semibold transition-colors',
                      fraction === f ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                    )}
                  >
                    {exitFractionLabel(f)}
                  </button>
                ))}
              </div>
              {/*
                A PREVIEW, AND SAID TO BE ONE. The lots come from the book
                this drawer last read; the request carries the percentage and
                the server sizes it against its own read at the moment of
                acting. They agree almost always, and when they do not the
                server's is the one that is right.
              */}
              {/*
                A basket states no lot count, because there is not one: the
                fraction is resolved against EACH leg's own open lots on the
                server, which is what keeps unequal legs in proportion. A
                single number here would be the sum of five different
                roundings and would not match any of them.
              */}
              <span className="num text-[9.5px] text-muted-foreground" data-testid="exit-size-note">
                {leg === null
                  ? direction === 'IN'
                    ? `${exitFractionLabel(fraction)} more of every leg, at market`
                    : `${exitFractionLabel(fraction)} of every leg, at market`
                  : direction === 'IN'
                    ? /*
                       * 9.61. An add states where it LANDS, not just what it
                       * sends. "1 lot" beside a holding of one is ambiguous
                       * about whether that is the new size or the addition,
                       * and this ticket sends orders without a confirm step.
                       */
                      `add ${addLots} lot${addLots === 1 ? '' : 's'} · ${(addLots * (leg.lotSize || 0)).toLocaleString('en-IN')} qty → ${openLots + addLots} lot${openLots + addLots === 1 ? '' : 's'} held${
                        openLots > 0 && addLots > openLots * fraction ? ' — one lot is the smallest add' : ''
                      }`
                    : `about ${lots} lot${lots === 1 ? '' : 's'} · ${qty.toLocaleString('en-IN')} qty${
                        lots >= openLots && fraction < 1 ? ' — the whole leg, at this size' : ''
                      }`}
              </span>
            </div>

            {direction === 'IN' && ratioShifts && (
              /*
                9.61. Rounding a percentage per leg does not always keep a
                ratio: a 2:1 spread scaled 25% adds 1:1 and comes out 3:2.
                Said BEFORE the order, because afterwards the card still
                carries the structure's name over a shape that is no
                longer quite it. The journal records what changed.
              */
              <p className="text-[10.5px] text-warning" data-testid="exit-ratio-warning">
                The legs will not all grow in proportion at this size, so the
                structure's shape shifts slightly. It is recorded in the journal.
              </p>
            )}
            {direction === 'IN' && armedCount > 0 && (
              /*
                9.62. A stop is sized when it is armed, so a leg that grows
                keeps one covering the old quantity. The supervisor
                withdraws it and arms a correct one — a real gap, briefly,
                and the trader is told before they create it.
              */
              <p className="text-[10.5px] text-warning" data-testid="exit-protection-warning">
                {armedCount === 1 ? 'A resting stop' : `${armedCount} resting stops`} will be
                replaced at the new size, which leaves {armedCount === 1 ? 'it' : 'them'}
                {' '}briefly unarmed.
              </p>
            )}

            {/*
              ── NO ORDER TYPE ON A BASKET — 9.36 ───────────────────────────
              A resting price only means something against ONE contract.
              Across five legs "limit 500" is five different trades wearing
              one instruction, and the premium a basket is worth is not a
              number any single leg's book can rest at. So a basket exits at
              market, and the choice is simply absent rather than present
              and refused.
            */}
            {!basket && (
            <div className="grid gap-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">How</span>
              <div
                className="grid grid-cols-2 gap-1 rounded-md border border-border bg-secondary/50 p-1"
                role="group"
                aria-label="Order type"
              >
                {/*
                  MARKET IS THE DEFAULT AND STAYS THE DEFAULT. "Exit" means
                  get me out; an order that rests is an order that may never
                  fill, which is a position the trader thinks they have
                  closed. It is offered because they asked for it, not
                  because it is the safer of the two.
                */}
                <button
                  type="button"
                  aria-pressed={!resting}
                  data-testid="exit-at-market"
                  onClick={() => setResting(false)}
                  className={cn(
                    'rounded py-1 text-[11px] font-semibold transition-colors',
                    !resting ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                  )}
                >
                  Market
                </button>
                <button
                  type="button"
                  aria-pressed={resting}
                  data-testid="exit-at-limit"
                  onClick={() => setResting(true)}
                  className={cn(
                    'rounded py-1 text-[11px] font-semibold transition-colors',
                    resting ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                  )}
                >
                  Limit
                </button>
              </div>
            </div>
            )}

            {!basket && resting && (
              <label className="grid gap-1.5 text-[11px] font-medium text-muted-foreground">
                Limit price (₹)
                <input
                  autoFocus
                  aria-label="Exit limit price"
                  data-testid="exit-limit-price"
                  className={cn(field, 'font-semibold text-foreground')}
                  type="number"
                  min={0.05}
                  step={0.05}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
                <span className="num text-[10px] font-normal">
                  {ltp === null ? 'Waiting for a quote' : `LTP ₹${ltp.toFixed(2)}`}
                </span>
              </label>
            )}
          </fieldset>

          {error && (
            <div
              role="alert"
              className="mt-2 rounded border border-destructive/20 bg-destructive/5 p-1.5 text-[10.5px] text-destructive"
            >
              {error}
            </div>
          )}

          <footer className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="rounded-md border border-border px-2.5 py-1.5 text-[11.5px] font-medium disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (leg === null ? held.length === 0 : openLots <= 0)}
              data-testid="exit-ticket-submit"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11.5px] font-semibold text-white disabled:opacity-50',
                /*
                  9.61. BLUE FOR IN, and it outranks the side colour. On the
                  way out the button is tinted by which side closes the leg
                  — green to buy back a short, red to sell a long — which is
                  useful and is the SECOND question. Which direction the
                  position is about to move is the first, and a trader who
                  reads the colour before the words must not read "add" as
                  "close".
                */
                direction === 'IN'
                  ? 'bg-primary'
                  : closingSide === 'BUY'
                    ? 'bg-success'
                    : 'bg-destructive',
              )}
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving
                ? 'Sending…'
                : direction === 'IN'
                  ? `Add ${exitFractionLabel(fraction)}${resting ? ' at limit' : ''}`
                  : `Exit ${exitFractionLabel(fraction)}${!basket && resting ? ' at limit' : ''}`}
            </button>
          </footer>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
